import { React, useState } from "react";
import { Button, Card, Modal, Table, Tag, message } from "antd";

import { Typography } from "antd";
import { pdf } from "@react-pdf/renderer";
import { CertificatePDFDocument } from "./CertificatePDFDocument";
import { useCertificateUnitRows } from "./Utils/useCertificateUnitRows";
import {
  formatCreditValue,
  resolveBundledCreditsTotal,
} from "./Utils/certificateUnitRowsUtils";

const { Title } = Typography;
const { Text } = Typography;

export default function ViewCertificate({
  isShowCertificate,
  finalTokenData,
  yieldRecord,
  closeModal,
}) {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const {
    certificateDetails: yieldRecordCertificateDetails,
    certificateUnitRows,
    linkageRows,
    linkageLoading,
    linkageError,
  } = useCertificateUnitRows({
    isVisible: isShowCertificate,
    finalTokenUuid: finalTokenData?._id,
    certificateType: "standard",
    yieldRecord,
  });

  const handleDownload = async () => {
    if (isGeneratingPdf) {
      return;
    }

    if (linkageLoading) {
      message.warning("Certificate details are still loading.");
      return;
    }
    if (linkageError) {
      message.error(linkageError);
      return;
    }
    if (!Array.isArray(certificateUnitRows) || certificateUnitRows.length === 0) {
      message.error("Cannot generate certificate: missing NFRY linkage rows.");
      return;
    }

    setIsGeneratingPdf(true);

    try {
      const filename =
        (finalTokenData?.finaltoken_id
          ? "PTCB-" + finalTokenData.finaltoken_id
          : "DRY-RUN-XXXX") + ".pdf";

      const rows = certificateUnitRows;

      const blob = await pdf(
        <CertificatePDFDocument
          yieldRecordCertificateDetails={yieldRecordCertificateDetails}
          finalTokenData={finalTokenData}
          certificateUnitRows={rows}
          bundledCreditsTotal={
            resolveBundledCreditsTotal(
              yieldRecordCertificateDetails[0] || {},
              linkageRows
            )
          }
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setIsGeneratingPdf(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div>
      <>
        {isShowCertificate && (
          <CertificateModal
            visible={isShowCertificate}
            finalTokenData={finalTokenData}
            yieldRecordCertificateDetails={yieldRecordCertificateDetails}
            certificateUnitRows={certificateUnitRows}
            linkageRows={linkageRows}
            linkageLoading={linkageLoading}
            linkageError={linkageError}
            handleDownload={handleDownload}
            isGeneratingPdf={isGeneratingPdf}
            onCancel={() => {
              closeModal();
            }}
          />
        )}
      </>
    </div>
  );
}

const CertificateModal = ({
  visible,
  onCancel,
  finalTokenData,
  yieldRecordCertificateDetails,
  certificateUnitRows,
  linkageRows,
  linkageLoading = false,
  linkageError = null,
  handleDownload,
  isGeneratingPdf = false,
}) => {
  function formatDateRange(dates) {
    if (!dates || dates.length < 2) return "Not specified";

    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    // Handle MongoDB date format with $date property
    const getTimestamp = (date) => {
      if (!date) return null;
      return date?.$date ? date.$date : date;
    };

    const startTimestamp = getTimestamp(dates[0]);
    const endTimestamp = getTimestamp(dates[1]);
    
    if (!startTimestamp || !endTimestamp) return "Not specified";

    try {
      const startDate = new Date(startTimestamp).toLocaleDateString("en-US", options);
      const endDate = new Date(endTimestamp).toLocaleDateString("en-US", options);
      return `${startDate} - ${endDate}`;
    } catch (e) {
      return "Not specified";
    }
  }

  function formatDate(date) {
    if (!date) return "-";
    // Handle MongoDB date format with $date property
    const timestamp = date?.$date ? date.$date : date;
    if (!timestamp) return "-";
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    try {
      return new Date(timestamp).toLocaleDateString("en-US", options);
    } catch (e) {
      return "-";
    }
  }

  const certificateData = yieldRecordCertificateDetails[0];
  const bundledCredits = resolveBundledCreditsTotal(certificateData || {}, linkageRows);

  return (
    <Modal
      width={1000}
      open={visible}
      cancelText="Close"
      onCancel={onCancel}
      footer={[
        <Button
          key="download"
          type="primary"
          onClick={handleDownload}
          loading={isGeneratingPdf}
          disabled={isGeneratingPdf || linkageLoading}
        >
          Download PDF
        </Button>,
      ]}
    >
      <Card
        style={{
          width: "100%",
          height: "100%",
          padding: "20px 30px",
        }}
        id="card-container"
      >
        <div style={{ display: "flex", justifyContent: "end", marginBottom: "24px" }}>
          <img src="/logo512.png" alt="logo" style={{ height: 36 }} />
        </div>
        <Title level={2} style={{ color: "#35B28D" }}>
          Proba Emission Reduction Certificate
        </Title>
        {linkageError && (
          <Text type="danger" style={{ display: "block", marginBottom: "12px" }}>
            NFRY linkage error: {linkageError}
          </Text>
        )}
        {yieldRecordCertificateDetails[0]?.ghg_impact_summary && (
          <>
            <Text style={{ display: "block", marginBottom: "16px" }}>
              {yieldRecordCertificateDetails[0]?.ghg_impact_summary}
            </Text>
          </>
        )}
        <Title level={4} style={{ color: "#35B28D", marginTop: "48px" }}>
          Certificate Details
        </Title>
        <Text>
          <strong>Certificate bundle ID: </strong>
          {finalTokenData?.finaltoken_id
            ? `PTCB-${finalTokenData.finaltoken_id}`
            : "DRY-RUN-XXXX"}
        </Text>
        <br />
        <Text>
          <strong>Bundle issuance date: </strong>
          {formatDate(finalTokenData?.created || new Date())}
        </Text>
        <br />
        {yieldRecordCertificateDetails[0]?.cropLabel && (
          <>
            <Text>
              <strong>Crop: </strong>
              {yieldRecordCertificateDetails[0]?.cropLabel}
            </Text>
            <br />
          </>
        )}
        {yieldRecordCertificateDetails[0]?.crop_yield_emissions?.totalCropYield != null && (
          <>
            <Text>
              <strong>Total crop yield: </strong>
              {Number(
                yieldRecordCertificateDetails[0].crop_yield_emissions.totalCropYield
              ).toFixed(0)} ton
            </Text>
            <br />
          </>
        )}

        {bundledCredits != null && (
          <>
            <Text>
              <strong>Bundled credits: </strong>
              {formatCreditValue(bundledCredits)}
            </Text>
            <br />
          </>
        )}

        <br />
        {certificateUnitRows.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: "24px" }}>
                Certified Emission Reduction Units
              </Title>
              <Text style={{ display: "block", marginBottom: "12px" }}>
                Each unit represents a verified quantity of emission reductions and is issued as a unique digital token recorded on the blockchain.
              </Text>
              <Table
                loading={linkageLoading}
                dataSource={certificateUnitRows}
                columns={[
                  {
                    title: (
                      <span style={{ whiteSpace: "nowrap" }}>ID</span>
                    ),
                    dataIndex: "tokenId",
                    key: "tokenId",
                    width: "12%",
                    render: (value) => (
                      <span style={{ whiteSpace: "nowrap" }}>
                        {value}
                      </span>
                    ),
                  },
                  {
                    title: "Token Creation Date",
                    dataIndex: "issuanceDate",
                    key: "issuanceDate",
                    width: "15%",
                  },
                  {
                    title: "Asset ID",
                    dataIndex: "assetId",
                    key: "assetId",
                    width: "15%",
                  },
                  {
                    title: "Period",
                    dataIndex: "period",
                    key: "period",
                    width: "15%",
                  },
                  {
                    title: "Product Volume",
                    dataIndex: "productVolume",
                    key: "productVolume",
                    width: "15%",
                  },
                  {
                    title: "Token GHG Mitigation",
                    dataIndex: "yield",
                    key: "yield",
                    width: "15%",
                  },
                  {
                    title: "Bundled Amount",
                    dataIndex: "bundledAmount",
                    key: "bundledAmount",
                    width: "15%",
                  },
                ]}
                pagination={false}
                size="small"
              />
            </>
          )}
        
        <Title level={4} style={{ color: "#35B28D", marginTop: "48px" }}>
          Project Details
        </Title>
        <Text>
          <strong>Project Name: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_name}
        </Text>
        <br />
        <Text>
          <strong>Location: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_region}
        </Text>
        <br />
        <Text>
          <strong>Project ID: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_project_id}
        </Text>
        <br />
        <Text>
          <strong>GHG Project Type: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_type === "removal"
            ? "Removal"
            : "Reduction"}
        </Text>
        <br />
        <Text>
          <strong>Standards Used: </strong>
          {Array.isArray(yieldRecordCertificateDetails[0]?.intervention_standards_used)
            ? yieldRecordCertificateDetails[0]?.intervention_standards_used.join(", ")
            : yieldRecordCertificateDetails[0]?.intervention_standards_used || "Proba Standard"}
        </Text>
        <br />
        <Text>
          <strong>GHG Project Summary: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_summary}
        </Text>
        <br />
        <Text>
          <strong>Crediting Period: </strong>
          {formatDateRange(
            yieldRecordCertificateDetails[0]?.intervention_crediting_period
          )}
        </Text>
        <br />
        <Text>
          <strong>Project Period: </strong>
          {formatDateRange(
            yieldRecordCertificateDetails[0]?.intervention_project_period
          )}
        </Text>
        <br />
        <div style={{ marginTop: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <Text style={{ fontWeight: "bold", fontSize: "13px", color: "#35B28D", marginBottom: "8px", display: "block" }}>
            Methodologies Applied:
          </Text>
          {Array.isArray(yieldRecordCertificateDetails[0]?.intervention_method) &&
          yieldRecordCertificateDetails[0].intervention_method.length > 0 ? (
            yieldRecordCertificateDetails[0].intervention_method.map(
              (method, idx) => (
                <Text key={idx} style={{ display: "block", marginLeft: "8px", marginBottom: "4px" }}>
                  • {method.id}: {method.name}
                </Text>
              )
            )
          ) : typeof yieldRecordCertificateDetails[0]?.intervention_method ===
            "string" ? (
            yieldRecordCertificateDetails[0].intervention_method
              .split(", ")
              .map((method, idx) => (
                <Text key={idx} style={{ display: "block", marginLeft: "8px", marginBottom: "4px" }}>
                  • {method}
                </Text>
              ))
          ) : (
            <Text style={{ marginLeft: "8px" }}>-</Text>
          )}
        </div>

        {yieldRecordCertificateDetails[0]?.emission_reduction_overview && (
          <>
            <Title level={4} style={{ color: "#35B28D", marginTop: "48px"}}>
              Emission Reduction Overview
            </Title>
            <Table
              dataSource={[
                {
                  key: "overview",
                  baseline:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.baseline ?? null,
                  project:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.project ?? null,
                  delta:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.delta ?? null,
                  reductionPercent:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.reduction_percent ?? null,
                  intensityBaseline:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.baseline ?? null,
                  intensityProject:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.project ?? null,
                  intensityReduction:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.reduction ?? null,
                },
              ]}
              columns={[
                {
                  title: (
                    <div>
                      <div>Baseline</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "baseline",
                  key: "baseline",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Project</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "project",
                  key: "project",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Reduction</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "delta",
                  key: "delta",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: "Reduction (%)",
                  dataIndex: "reductionPercent",
                  key: "reductionPercent",
                  width: "25%",
                  render: (value) =>
                    value != null ? `${Number(value).toFixed(1)} %` : "-",
                },
              ]}
              pagination={false}
              size="small"
            />
            <br />
            <Title level={5} style={{ marginTop: "4px" }}>
              Emission Intensity
            </Title>
            <Table
              dataSource={[
                {
                  key: "intensity",
                  baseline:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.baseline ?? null,
                  project:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.project ?? null,
                  reduction:
                    yieldRecordCertificateDetails[0].emission_reduction_overview
                      ?.emission_intensity?.reduction ?? null,
                },
              ]}
              columns={[
                {
                  title: (
                    <div>
                      <div>Baseline</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e / t crop</div>
                    </div>
                  ),
                  dataIndex: "baseline",
                  key: "baseline",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Project</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e / t crop</div>
                    </div>
                  ),
                  dataIndex: "project",
                  key: "project",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Reduction</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e / t crop</div>
                    </div>
                  ),
                  dataIndex: "reduction",
                  key: "reduction",
                  width: "25%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: "Reduction (%)",
                  dataIndex: "reduction",
                  key: "reductionPercent",
                  width: "25%",
                  render: (value, record) => {
                    if (value == null || record.baseline == null || record.baseline === 0) {
                      return "-";
                    }
                    const percent = (value / record.baseline) * 100;
                    return `${Number(percent).toFixed(1)} %`;
                  },
                },
              ]}
              pagination={false}
              size="small"
            />
            <br />
          </>
        )}

        {yieldRecordCertificateDetails[0]?.emission_breakdown && (
          <>
            <Title level={4} style={{ color: "#35B28D", marginTop: "48px" }}>
              Emission Breakdown
            </Title>
            <Title level={5} style={{ marginTop: "8px", marginBottom: "8px" }}>
              By Fertilizer Emission Type
            </Title>
            <Table
              dataSource={yieldRecordCertificateDetails[0].emission_breakdown.byEmissionType.map(
                (row, idx) => ({
                  key: idx,
                  component: row.component,
                  baseline: row.baseline,
                  project: row.project,
                  delta: row.delta,
                  deltaPerTon: row.deltaPerTon,
                })
              )}
              columns={[
                {
                  title: "Component",
                  dataIndex: "component",
                  key: "component",
                  width: "30%",
                  render: (text, record) =>
                    record.component === "Total" ? (
                      <strong>{text}</strong>
                    ) : (
                      text
                    ),
                },
                {
                  title: (
                    <div>
                      <div>Baseline</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "baseline",
                  key: "baseline",
                  width: "17.5%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Project</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "project",
                  key: "project",
                  width: "17.5%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Emission Reduction</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "delta",
                  key: "delta",
                  width: "17.5%",
                  render: (value, record) =>
                    value != null ? (
                      <span style={record.component === "Total" ? { fontWeight: "bold" } : {}}>
                        {Number(value).toFixed(3)}
                      </span>
                    ) : (
                      "-"
                    ),
                },
                {
                  title: (
                    <div>
                      <div>Reduction Intensity</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e / t crop</div>
                    </div>
                  ),
                  dataIndex: "deltaPerTon",
                  key: "deltaPerTon",
                  width: "17.5%",
                  render: (value, record) =>
                    value != null ? (
                      <span style={record.component === "Total" ? { fontWeight: "bold" } : {}}>
                        {Number(value).toFixed(3)}
                      </span>
                    ) : (
                      "-"
                    ),
                },
              ]}
              pagination={false}
              size="small"
            />
            <br />
            <Title level={5} style={{ marginTop: "8px", marginBottom: "8px" }}>
              By Input Type
            </Title>
            <Table
              dataSource={yieldRecordCertificateDetails[0].emission_breakdown.byInputType.map(
                (row, idx) => ({
                  key: idx,
                  inputType: row.inputType,
                  includesInhibitor: row.includesInhibitor,
                  baseline: row.baseline,
                  project: row.project,
                  delta: row.delta,
                  deltaPerTon: row.deltaPerTon,
                })
              )}
              columns={[
                {
                  title: "Input Type",
                  dataIndex: "inputType",
                  key: "inputType",
                  width: "30%",
                  render: (text, record) =>
                    record.inputType === "Total" ? (
                      <strong>{text}</strong>
                    ) : record.inputType === "Fertilizers" &&
                      record.includesInhibitor ? (
                      <span>
                        {text}{" "}
                        <Tag style={{ marginLeft: 4 }}>+ Inhibitor</Tag>
                      </span>
                    ) : (
                      text
                    ),
                },
                {
                  title: (
                    <div>
                      <div>Baseline</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "baseline",
                  key: "baseline",
                  width: "17.5%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Project</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "project",
                  key: "project",
                  width: "17.5%",
                  render: (value) =>
                    value != null ? Number(value).toFixed(3) : "-",
                },
                {
                  title: (
                    <div>
                      <div>Emission Reduction</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e</div>
                    </div>
                  ),
                  dataIndex: "delta",
                  key: "delta",
                  width: "17.5%",
                  render: (value, record) =>
                    value != null ? (
                      <span style={record.inputType === "Total" ? { fontWeight: "bold" } : {}}>
                        {Number(value).toFixed(3)}
                      </span>
                    ) : (
                      "-"
                    ),
                },
                {
                  title: (
                    <div>
                      <div>Reduction Intensity</div>
                      <div style={{ fontSize: "11px", fontWeight: "normal" }}>tCO₂e / t crop</div>
                    </div>
                  ),
                  dataIndex: "deltaPerTon",
                  key: "deltaPerTon",
                  width: "17.5%",
                  render: (value, record) =>
                    value != null ? (
                      <span style={record.inputType === "Total" ? { fontWeight: "bold" } : {}}>
                        {Number(value).toFixed(3)}
                      </span>
                    ) : (
                      "-"
                    ),
                },
              ]}
              pagination={false}
              size="small"
            />
            <br />
          </>
        )}

        <Title level={4} style={{ color: "#35B28D", marginTop: "24px", marginBottom: "8px" }}>
          Governance and Assurance
        </Title>
        <Text>
          <strong>Project Developer: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_operator}
        </Text>
        <br />
        {yieldRecordCertificateDetails[0]?.intervention_project_sponsor && (
          <>
            <Text>
              <strong>Project Sponsor: </strong>
              {yieldRecordCertificateDetails[0]?.intervention_project_sponsor}
            </Text>
            <br />
          </>
        )}
        <Text>
          <strong>Validator: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_validator_of_quality}
        </Text>
        <br />
        <Text>
          <strong>Verifier: </strong>
          {yieldRecordCertificateDetails[0]?.intervention_verifier_of_quality}
        </Text>
        <br />
      </Card>
    </Modal>
  );
};
